/**
 * TrainingProgress — "training academy" checklist card for the ops manual.
 *
 * Everyone sees their own progress bar across all mapped training videos
 * (both manuals). Admins additionally get a per-staff overview table for
 * onboarding compliance.
 */
import React, { useEffect, useMemo, useState } from 'react';
import { GraduationCap, CheckCircle2 } from 'lucide-react';
import { api } from '../../lib/api';
import { useAuth } from '../../lib/auth';
import { MANUAL, SETUP_STEPS } from './content';

const EXCLUDED_KEYS = new Set(['patient-welcome']);

function buildTitleMap() {
  const m = {};
  MANUAL.forEach(g => g.sections.forEach(s => { m[s.id] = s.title; }));
  SETUP_STEPS.forEach(s => { m[s.id] = s.title; });
  return m;
}

function Bar({ done, total }) {
  const pct = total ? Math.round((done / total) * 100) : 0;
  return (
    <div className="flex items-center gap-2 min-w-[140px]">
      <div className="flex-1 h-2 rounded-full bg-fg/10 overflow-hidden">
        <div className={`h-full rounded-full transition-all ${pct === 100 ? 'bg-[#2E7D4F]' : 'bg-brand'}`} style={{ width: `${pct}%` }}/>
      </div>
      <span className="text-xs text-fg-muted whitespace-nowrap font-mono">{done}/{total}</span>
    </div>
  );
}

export function TrainingProgress() {
  const { user } = useAuth();
  const isAdmin = user?.role === 'admin';
  const [videoKeys, setVideoKeys] = useState([]);
  const [watched, setWatched] = useState(new Set());
  const [staff, setStaff] = useState(null);
  const titles = useMemo(buildTitleMap, []);

  useEffect(() => {
    api.get('/clinic/settings').then(r => {
      const keys = Object.keys(r.data?.setup_videos || {}).filter(k => !EXCLUDED_KEYS.has(k) && titles[k]);
      setVideoKeys(keys);
    }).catch(()=>{});
    api.get('/training/me').then(r => setWatched(new Set(r.data?.watched || []))).catch(()=>{});
    if (isAdmin) api.get('/training/overview').then(r => setStaff(r.data?.staff || [])).catch(()=>{});
  }, [isAdmin, titles]);

  if (videoKeys.length === 0) return null;

  const myDone = videoKeys.filter(k => watched.has(k)).length;
  const unwatched = videoKeys.filter(k => !watched.has(k));

  return (
    <div className="surface border border-soft rounded-xl p-5 mb-6" data-testid="training-progress-card">
      <div className="flex flex-wrap items-center gap-4">
        <div className="flex items-center gap-2">
          <GraduationCap className="w-5 h-5 text-brand"/>
          <h2 className="font-heading font-semibold text-lg">Training progress</h2>
        </div>
        <div className="flex-1 min-w-[200px]" data-testid="training-own-bar">
          <Bar done={myDone} total={videoKeys.length}/>
        </div>
        {myDone === videoKeys.length && (
          <span className="inline-flex items-center gap-1 text-xs text-[#2E7D4F] font-medium">
            <CheckCircle2 className="w-4 h-4"/> All training videos watched
          </span>
        )}
      </div>
      <p className="text-xs text-fg-muted mt-1.5">
        Videos are marked watched automatically when they finish playing — or use the "Mark watched" button under any player.
      </p>

      {unwatched.length > 0 && (
        <div className="mt-3 flex flex-wrap gap-1.5" data-testid="training-unwatched-chips">
          <span className="text-xs text-fg-muted mr-1 mt-0.5">Still to watch:</span>
          {unwatched.slice(0, 6).map(k => (
            <a key={k} href={`#${k}`}
               className="text-xs px-2.5 py-1 rounded-full border border-soft text-fg-secondary hover:border-brand/50 hover:text-fg transition"
               data-testid={`training-chip-${k}`}>
              {titles[k]}
            </a>
          ))}
          {unwatched.length > 6 && <span className="text-xs text-fg-muted mt-0.5">+{unwatched.length - 6} more</span>}
        </div>
      )}

      {isAdmin && staff && staff.length > 0 && (
        <div className="mt-5 border-t border-soft pt-4">
          <h3 className="text-xs uppercase tracking-wider text-fg-muted mb-2">Team overview — onboarding compliance</h3>
          <table className="w-full text-sm" data-testid="training-overview-table">
            <tbody>
              {staff.map(s => {
                const done = videoKeys.filter(k => (s.watched || []).includes(k)).length;
                return (
                  <tr key={s.user_id} className="border-b border-soft/50 last:border-0" data-testid={`training-row-${s.user_id}`}>
                    <td className="py-2 pr-3">
                      <div className="font-medium text-fg">{s.name || s.email}</div>
                      <div className="text-[11px] text-fg-muted capitalize">{s.role}</div>
                    </td>
                    <td className="py-2 w-[45%]"><Bar done={done} total={videoKeys.length}/></td>
                    <td className="py-2 pl-3 text-right">
                      {done === videoKeys.length
                        ? <span className="inline-flex items-center gap-1 text-xs text-[#2E7D4F]"><CheckCircle2 className="w-3.5 h-3.5"/> Complete</span>
                        : <span className="text-xs text-fg-muted">{videoKeys.length - done} remaining</span>}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
